import React, { useState, useEffect, useRef } from 'react';
import {
  Send, Activity, CheckCircle, AlertCircle, Loader2,
  Monitor, Smartphone, ChevronDown
} from 'lucide-react';
import FCMForm from '../components/FCMForm';
import EmailForm from '../components/EmailForm';
import SMSForm from '../components/SMSForm';
import FCMHistory from './FCMHistory';
import EmailHistory from './EmailHistory';
import SMSHistory from './SMSHistory';
import AppBasedStats from '../components/AppBasedStats';
import FCMQueueStats from './FCMQueueStats';
import DuplicateLoginModal from '../components/DuplicateLoginModal';
import FCMConfig from '../components/FCMConfig';
import SMSConfig from '../components/SMSConfig';
import EmailConfig from '../components/EmailConfig';
import Layout from '../components/Layout';
import ApiDocs from '../components/ApiDocs';
import { apiFetch } from '../api';
import StatNumberWithTooltip from '../components/StatNumberWithTooltip';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';
import ModeSwitchModal from '../components/ModeSwitchModal';
import Logs from './Logs';
import SmsTemplateManager from './SmsTemplateManager';
import WhatsappTemplateManager from '../components/whatsapp/Whatsapptemplatemanager';
import WhatsAppConfig from '../components/whatsapp/Whatsappconfig';
import WhatsAppForm from '../components/whatsapp/Whatsappform';
import WhatsAppHistory from '../components/whatsapp/WhatsAppHistory';
import NotificationSearchById from '../components/Notificationsearchbyid';

export default function Dashboard({ email, onLogout }) {

  const { duplicateLogin, clearDuplicateFlag, client, deliveryMode, setDeliveryMode } = useWebSocket();

  const [activeTab, setActiveTab] = useState('push');
  const [isLoading, setIsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState('');
  const [showAppStats, setShowAppStats] = useState(false);
  const [showQueueStats, setShowQueueStats] = useState(false);
  const [showConfig, setShowConfig] = useState('');
  const [showApiDocs, setShowApiDocs] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [targetMode, setTargetMode] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [showSmsTemplates, setShowSmsTemplates] = useState(false);
  const [showWhatsAppTemplates, setShowWhatsAppTemplates] = useState(false);
  const [showSearchById, setShowSearchById] = useState(false); // ← NEW

  const [isLiveDataEnabled, setIsLiveDataEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [showIntervalDropdown, setShowIntervalDropdown] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const dropdownRef = useRef(null);

  const intervalOptions = [
    { value: 15, label: '15 sec' }, { value: 30, label: '30 sec' },
    { value: 45, label: '45 sec' }, { value: 60, label: '1 min' },
    { value: 300, label: '5 min' }, { value: 600, label: '10 min' },
    { value: 900, label: '15 min' }
  ];

  const [stats, setStats] = useState({ totalSent: 0, delivered: 0, pending: 0, failed: 0 });

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const currentMonth = monthNames[new Date().getMonth()];

  const ENV = process.env.REACT_APP_ENV;
  const API_BASE_URL = ENV === 'dev' ? process.env.REACT_APP_API_BASE_URL : window.location.origin;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowIntervalDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let id;
    if (isLiveDataEnabled && countdown > 0) {
      id = setInterval(() => setCountdown(prev => prev <= 1 ? refreshInterval : prev - 1), 1000);
    }
    return () => { if (id) clearInterval(id); };
  }, [isLiveDataEnabled, countdown, refreshInterval]);

  const formatCountdown = (s) => s >= 60
    ? `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`
    : `${s}s`;

  const toggleLiveData = () => {
    const next = !isLiveDataEnabled;
    setIsLiveDataEnabled(next);
    setCountdown(next ? refreshInterval : 0);
  };

  const handleIntervalChange = (value) => {
    setRefreshInterval(value);
    setShowIntervalDropdown(false);
    if (isLiveDataEnabled) setCountdown(value);
  };

  // ── Reset ──────────────────────────────────────────────────────────────────
  const resetToDashboard = () => {
    setShowHistory('');
    setShowConfig('');
    setShowAppStats(false);
    setShowQueueStats(false);
    setShowApiDocs(false);
    setShowLogs(false);
    setShowSmsTemplates(false);
    setShowWhatsAppTemplates(false);
    setShowSearchById(false); // ← NEW
  };

  // ── Fetch Stats ────────────────────────────────────────────────────────────
  const fetchStats = async (type) => {
    if (!isLiveDataEnabled) setStatsLoading(true);
    setError('');
    try {
      if (deliveryMode === 'socket') {
        try { await ensureSocketConnection(client); }
        catch { setError('Socket connection failed.'); setStatsLoading(false); return; }

        const socketMap = {
          push: { destination: '/app/get/fcm/count', topic: `/topic/get/fcm/count/${email}` },
          sms: { destination: '/app/get/sms/count', topic: `/topic/get/sms/count/${email}` },
          email: { destination: '/app/get/email/count', topic: `/topic/get/email/count/${email}` },
          whatsapp: { destination: '/app/whatsapp/count/month', topic: `/topic/whatsapp/count/month/${email}` },
        };
        const { destination, topic } = socketMap[type] || socketMap.push;
        const sub = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
            if (data?.STATUS === 'SUCCESS') {
              setStats({ totalSent: data.count || 0, delivered: data.Delivered_Count || 0, pending: data.Pending_Count || 0, failed: data.Falied_Count || 0 });
            }
          } catch { setError(`Failed to parse ${type} statistics.`); }
          finally { sub.unsubscribe(); setStatsLoading(false); }
        });
        client.publish({ destination, body: JSON.stringify({}) });

      } else {
        const endpointMap = { push: '/get/fcm/count', sms: '/get/sms/count', email: '/get/email/count', whatsapp: '/whatsapp/count/month' };
        const res = await apiFetch(endpointMap[type] || endpointMap.push);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.STATUS === 'SUCCESS') {
          setStats({ totalSent: data.count || 0, delivered: data.Delivered_Count || 0, pending: data.Pending_Count || 0, failed: data.Falied_Count || 0 });
        } else throw new Error(data.MESSAGE || 'Unsuccessful');
        setStatsLoading(false);
      }
    } catch {
      if (!isLiveDataEnabled) {
        const lbl = { push: 'FCM', sms: 'SMS', email: 'Email', whatsapp: 'WhatsApp' };
        setError(`Failed to fetch ${lbl[type] || type} statistics.`);
      }
      setStats({ totalSent: 0, delivered: 0, pending: 0, failed: 0 });
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    let id;
    if (isLiveDataEnabled && activeTab) {
      id = setInterval(() => { fetchStats(activeTab); setCountdown(refreshInterval); }, refreshInterval * 1000);
    }
    return () => { if (id) clearInterval(id); };
  }, [isLiveDataEnabled, refreshInterval, activeTab, deliveryMode]);

  useEffect(() => { fetchStats(activeTab); }, [activeTab, deliveryMode, email]);

  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(''); setError(''); }, 3000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  const tabs = [
    { id: 'push', label: 'Push Notifications', color: 'from-blue-500 to-cyan-500' },
    { id: 'email', label: 'Email', color: 'from-green-500 to-emerald-500' },
    { id: 'sms', label: 'SMS', color: 'from-purple-500 to-pink-500' },
    { id: 'whatsapp', label: 'WhatsApp', color: 'from-emerald-500 to-teal-500' },
  ];

  const statsCards = [
    { title: 'Total Sent', value: stats.totalSent, icon: Send, bgColor: 'bg-blue-500/10', textColor: 'text-blue-400' },
    { title: 'Delivered', value: stats.delivered, icon: CheckCircle, bgColor: 'bg-green-500/10', textColor: 'text-green-400' },
    { title: 'Pending', value: stats.pending, icon: Activity, bgColor: 'bg-yellow-500/10', textColor: 'text-yellow-400' },
    { title: 'Failed', value: stats.failed, icon: AlertCircle, bgColor: 'bg-red-500/10', textColor: 'text-red-400' }
  ];

  const getQueueStatusText = () => ({
    push: 'real-time FCM statistics', email: 'real-time Email queue statistics',
    sms: 'real-time SMS statistics', whatsapp: 'real-time WhatsApp statistics',
  }[activeTab] || 'real-time statistics');

  // ── Layout props ───────────────────────────────────────────────────────────
  const layoutProps = {
    email, onLogout, activeTab, setActiveTab,
    showHistory, setShowHistory,
    showAppStats, setShowAppStats,
    showConfig, setShowConfig,
    showQueueStats, setShowQueueStats,
    showApiDocs, setShowApiDocs,
    showLogs, setShowLogs,
    showSmsTemplates, setShowSmsTemplates,
    showWhatsAppTemplates, setShowWhatsAppTemplates,
    showSearchById, setShowSearchById, // ← NEW
  };

  // ── Routes ─────────────────────────────────────────────────────────────────
  if (showConfig === 'fcm') return <Layout {...layoutProps}><FCMConfig onBack={resetToDashboard} setError={setError} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showConfig === 'sms') return <Layout {...layoutProps}><SMSConfig onBack={resetToDashboard} setError={setError} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showConfig === 'email') return <Layout {...layoutProps}><EmailConfig onBack={resetToDashboard} setError={setError} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showConfig === 'whatsapp') return <Layout {...layoutProps}><WhatsAppConfig onBack={resetToDashboard} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} /></Layout>;

  if (showHistory === 'fcm') return <Layout {...layoutProps}><FCMHistory onBack={resetToDashboard} setError={setError} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showHistory === 'email') return <Layout {...layoutProps}><EmailHistory onBack={resetToDashboard} setError={setError} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showHistory === 'sms') return <Layout {...layoutProps}><SMSHistory onBack={resetToDashboard} setError={setError} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showHistory === 'whatsapp') return <Layout {...layoutProps}><WhatsAppHistory onBack={resetToDashboard} setError={setError} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} /></Layout>;

  if (showAppStats) return <Layout {...layoutProps}><AppBasedStats onBack={resetToDashboard} setError={setError} API_BASE_URL={API_BASE_URL} activeTab={activeTab} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showQueueStats) return <Layout {...layoutProps}><FCMQueueStats onBack={resetToDashboard} setError={setError} API_BASE_URL={API_BASE_URL} activeTab={activeTab} loginId={email} deliveryMode={deliveryMode} /></Layout>;
  if (showSmsTemplates) return <Layout {...layoutProps}><SmsTemplateManager onBack={resetToDashboard} /></Layout>;
  if (showWhatsAppTemplates) return <Layout {...layoutProps}><WhatsappTemplateManager onBack={resetToDashboard} /></Layout>;
  if (showApiDocs) return <Layout {...layoutProps}><ApiDocs onBack={resetToDashboard} setError={setError} /></Layout>;
  if (showLogs) return <Layout {...layoutProps}><Logs loginId={email} deliveryMode={deliveryMode} onBack={() => setShowLogs(false)} /></Layout>;

  // ← NEW: Search by ID route
  if (showSearchById) return (
    <Layout {...layoutProps}>
      <NotificationSearchById
        onBack={resetToDashboard}
        setError={setError}
        loginId={email}
        deliveryMode={deliveryMode}
      />
    </Layout>
  );

  // ── Main Dashboard ─────────────────────────────────────────────────────────
  return (
    <Layout {...layoutProps}>
      <div className="p-3 sm:p-4 lg:p-6 xl:p-8">

        {/* Desktop Top Bar */}
        <div className="hidden lg:flex justify-between items-center mb-6 xl:mb-8 relative z-50">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3 px-4 py-2 bg-white/5 rounded-2xl border border-white/10">
              <Smartphone className="w-5 h-5 text-purple-400" />
              <span className="text-white font-medium">Notification Dashboard</span>
            </div>
            <div onClick={() => { resetToDashboard(); setShowQueueStats(true); }} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') { resetToDashboard(); setShowQueueStats(true); } }}
              className="cursor-pointer flex items-center space-x-3 px-4 py-2 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
              <Monitor className="w-5 h-5 text-purple-400" />
              <span className="text-white font-medium">{getQueueStatusText()}</span>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {deliveryMode === 'socket' && (
              <div className="flex items-center space-x-2 bg-white/5 backdrop-blur-md border border-white/20 rounded-lg px-3 py-1.5">
                <span className={`text-xs font-medium ${isLiveDataEnabled ? 'text-white' : 'text-white/50'}`}>Live</span>
                <button onClick={toggleLiveData} className={`relative w-11 h-6 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${isLiveDataEnabled ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gray-600'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-lg transform transition-all duration-300 flex items-center justify-center ${isLiveDataEnabled ? 'translate-x-5' : 'translate-x-0'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${isLiveDataEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                  </span>
                </button>
                {isLiveDataEnabled && countdown > 0 && (
                  <div className="flex items-center space-x-1 bg-green-500/10 px-2 py-1 rounded">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-xs text-green-400 font-mono font-semibold min-w-[32px]">{formatCountdown(countdown)}</span>
                  </div>
                )}
                <div className="relative" ref={dropdownRef}>
                  <button onClick={() => setShowIntervalDropdown(!showIntervalDropdown)} className="flex items-center space-x-1 px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 rounded text-xs transition-all">
                    <span>{intervalOptions.find(o => o.value === refreshInterval)?.label}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showIntervalDropdown && (
                    <div className="absolute right-0 mt-2 min-w-[6rem] bg-slate-900 border border-white/20 rounded-lg shadow-xl overflow-hidden z-[100]">
                      {intervalOptions.map(o => (
                        <button key={o.value} onClick={() => handleIntervalChange(o.value)}
                          className={`w-full px-4 py-2 text-left text-xs hover:bg-purple-500/20 transition-colors block ${refreshInterval === o.value ? 'bg-purple-500/30 text-purple-200' : 'text-white/70'}`}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <span className="text-xs text-white/60 uppercase tracking-wide">Mode</span>
              <div className="flex items-center bg-white/5 border border-white/10 rounded-full p-1">
                <button type="button" onClick={() => { setTargetMode('api'); setShowModeModal(true); }}
                  className={`px-3 py-1 text-xs rounded-full transition-all ${deliveryMode === 'api' ? 'bg-indigo-500 text-white shadow-sm' : 'text-white/60 hover:text-white'}`}>API based</button>
                <button type="button" onClick={() => { setTargetMode('socket'); setShowModeModal(true); }}
                  className={`px-3 py-1 text-xs rounded-full transition-all ${deliveryMode === 'socket' ? 'bg-indigo-500 text-white shadow-sm' : 'text-white/60 hover:text-white'}`}>Stream based</button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="lg:hidden mb-4 sm:mb-6">
          <button onClick={() => { resetToDashboard(); setShowQueueStats(true); }}
            className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
            <Monitor className="w-5 h-5 text-purple-400" />
            <span className="text-white font-medium text-sm sm:text-base">View Queue Statistics</span>
          </button>
        </div>

        {/* Stats */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-200 mb-4">{currentMonth} Stats</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {statsCards.map((card) => (
              <div key={card.title} className="relative group bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 lg:p-6 hover:bg-white/10 transition-all duration-300 transform hover:scale-105 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl sm:rounded-2xl" />
                {statsLoading && !isLiveDataEnabled && (
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center z-10">
                    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 text-white animate-spin" />
                  </div>
                )}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between relative z-10">
                  <div className="mb-3 lg:mb-0">
                    <p className="text-white/60 text-sm sm:text-base mb-1">{card.title}</p>
                    <p className={`text-lg sm:text-2xl lg:text-3xl font-bold ${card.textColor}`}>
                      {statsLoading && !isLiveDataEnabled ? '...' : <StatNumberWithTooltip value={card.value} />}
                    </p>
                    <div className="w-12 sm:w-14 h-1 bg-gradient-to-r from-current to-transparent rounded-full opacity-60 mt-1" />
                  </div>
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 ${card.bgColor} rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/10`}>
                    <card.icon className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 ${card.textColor}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Panel */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl">
          <div className="flex items-center mb-6 sm:mb-8">
            {tabs.find(t => t.id === activeTab) && (
              <>
                <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-r ${tabs.find(t => t.id === activeTab).color} rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg`}>
                  <Send className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-1">Send {tabs.find(t => t.id === activeTab).label}</h2>
                  <p className="text-white/60 text-sm sm:text-base">Create and send {tabs.find(t => t.id === activeTab).label.toLowerCase()} to your users</p>
                </div>
              </>
            )}
          </div>

          {(success || error) && (
            <div className={`mb-6 sm:mb-8 p-3 sm:p-4 rounded-xl sm:rounded-2xl flex items-start sm:items-center space-x-3 border backdrop-blur-sm ${success ? 'bg-green-500/10 border-green-500/30 text-green-200' : 'bg-red-500/10 border-red-500/30 text-red-200'}`}>
              {success ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 sm:mt-0" /> : <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 sm:mt-0" />}
              <span className="font-medium text-sm sm:text-base">{success || error}</span>
            </div>
          )}

          <div className="space-y-4 sm:space-y-6">
            {activeTab === 'push' && <FCMForm setIsLoading={setIsLoading} setSuccess={setSuccess} setError={setError} fetchStats={fetchStats} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} />}
            {activeTab === 'email' && <EmailForm setIsLoading={setIsLoading} setSuccess={setSuccess} setError={setError} fetchStats={fetchStats} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} />}
            {activeTab === 'sms' && <SMSForm setIsLoading={setIsLoading} setSuccess={setSuccess} setError={setError} fetchStats={fetchStats} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} />}
            {activeTab === 'whatsapp' && <WhatsAppForm setIsLoading={setIsLoading} setSuccess={setSuccess} setError={setError} fetchStats={fetchStats} API_BASE_URL={API_BASE_URL} loginId={email} deliveryMode={deliveryMode} />}
          </div>
        </div>
      </div>

      <ModeSwitchModal
        isOpen={showModeModal}
        onClose={() => setShowModeModal(false)}
        currentMode={deliveryMode}
        targetMode={targetMode}
        onConfirm={(newMode) => setDeliveryMode(newMode || targetMode)}
        email={email}
      />
    </Layout>
  );
}